'use client'

import React from 'react'
import { cn } from '../../../lib/utils'
import { Icon } from '../Icon'

export interface TerminalLine {
  value: string
  date: Date
}

export interface TerminalProps {
  lines: TerminalLine[]
  label?: string
  expandable?: boolean
  defaultExpanded?: boolean
  className?: string
}

// Helper function to detect if a string is valid JSON
const isJSON = (str: string): boolean => {
  try {
    JSON.parse(str)
    return true
  } catch (e) {
    return false
  }
}

// Maximum length for displayed JSON before truncation
const MAX_JSON_DISPLAY_LENGTH = 200

// Maximum length before treating a string as a long HTML component
const MAX_HTML_DISPLAY_LENGTH = 200

// Basic HTML detector that looks for common tags
const isHtmlString = (value: string): boolean => {
  const htmlTagPattern =
    /<\s*(table|div|span|p|tr|td|th|tbody|thead|html|body|section|article|header|footer)\b/i
  return htmlTagPattern.test(value)
}

// Helper function to pretty print JSON with highlighted keys
const formatJSONWithHighlight = (jsonString: string): React.ReactNode => {
  try {
    const parsed = JSON.parse(jsonString)
    const formatted = JSON.stringify(parsed, null, 2)

    // Regular expression to identify JSON keys with their quotes and colon
    const regex = /"([^"]+)":/g

    // Split the JSON string by the regex to separate keys from values
    const parts = formatted.split(regex)

    // Build the result with highlighted keys
    const result: React.ReactNode[] = []

    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        // This is a value or structural part (brackets, commas)
        result.push(<span key={`value-${i}`}>{parts[i]}</span>)
      } else {
        // This is a key
        result.push(
          <span
            key={`key-${i}`}
            className="font-semibold text-blue-600 dark:text-blue-400"
          >
            "{parts[i]}":
          </span>,
        )
      }
    }

    return <>{result}</>
  } catch (e) {
    return <span>{jsonString}</span>
  }
}

const Terminal: React.FC<TerminalProps> = ({
  lines,
  label,
  className,
  expandable = true,
  defaultExpanded = false,
}) => {
  // State to track expanded/collapsed state for JSON lines
  const [expandedLines, setExpandedLines] = React.useState<
    Record<number, boolean>
  >(() => {
    if (defaultExpanded) {
      return lines.reduce((acc, _, index) => ({ ...acc, [index]: true }), {})
    }
    return {}
  })

  // Toggle expanded state for a specific line
  const toggleLineExpansion = (index: number) => {
    if (!expandable) return
    setExpandedLines((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  return (
    <div className={cn(className)} onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-col gap-2">
        {label && (
          <div className="flex flex-row items-center gap-2 underline underline-offset-4 decoration-border decoration-dotted">
            <Icon name={'Terminal'} size={16} />
            <span className="text-xs font-medium">{label}</span>
          </div>
        )}
        <div className="text-xs text-muted-foreground font-mono border border-border bg-muted/30 p-2 rounded-md shadow-sm overflow-auto">
          {lines.map((line, index) => {
            const isJsonLine = isJSON(line.value)
            const isLongHtml =
              !isJsonLine &&
              line.value.length > MAX_HTML_DISPLAY_LENGTH &&
              isHtmlString(line.value)

            // Only check length for JSON lines
            const isLongJson =
              isJsonLine && line.value.length > MAX_JSON_DISPLAY_LENGTH
            const isExpanded = !!expandedLines[index]

            return (
              <div key={index} className="flex flex-col gap-1">
                <div className="flex flex-row items-start gap-2">
                  {isJsonLine ? (
                    <pre className="whitespace-pre-wrap break-words text-foreground/80">
                      {isExpanded
                        ? formatJSONWithHighlight(line.value)
                        : formatJSONWithHighlight(
                            line.value.substring(0, MAX_JSON_DISPLAY_LENGTH) +
                              '...',
                          )}
                    </pre>
                  ) : (
                    <>
                      {isLongHtml && isExpanded ? (
                        <pre className="whitespace-pre-wrap break-words text-foreground/80">
                          {line.value}
                        </pre>
                      ) : (
                        <span className="whitespace-pre-wrap break-words">
                          {isLongHtml ? 'View Content' : line.value}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {(isJsonLine || isLongHtml) && expandable && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleLineExpansion(index)
                    }}
                    className="flex items-center text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 text-xs mt-1"
                  >
                    <Icon
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      size={14}
                      className="mr-1"
                    />
                    {isLongHtml
                      ? isExpanded
                        ? 'Hide Content'
                        : 'View Content'
                      : isExpanded
                        ? 'Collapse JSON'
                        : 'Expand JSON'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Terminal
